<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';

const emit = defineEmits(['create', 'edit', 'close']);

const store = useStore();
const { t } = useI18n();

const {
  accountLabels,
  savedLabels,
  addLabelToConversation,
  removeLabelFromConversation,
} = useConversationLabels();

const currentChat = useMapGetter('getSelectedChat');
const hasOpenConversation = computed(() => Boolean(currentChat.value?.id));

const workspaceLabels = computed(() =>
  accountLabels.value.filter(label => label.label_type !== 'private')
);
const personalLabels = computed(() =>
  accountLabels.value.filter(label => label.label_type === 'private')
);

const isAssigned = label => savedLabels.value.includes(label.title);

function toggleAssign(label) {
  if (!hasOpenConversation.value) return;
  if (isAssigned(label)) {
    removeLabelFromConversation(label.title);
  } else {
    addLabelToConversation(label);
  }
}

// Delete flow
const showDeleteConfirm = ref(false);
const labelToDelete = ref(null);

function openDelete(label) {
  labelToDelete.value = label;
  showDeleteConfirm.value = true;
}

function closeDelete() {
  showDeleteConfirm.value = false;
  labelToDelete.value = null;
}

async function confirmDelete() {
  try {
    await store.dispatch('labels/delete', labelToDelete.value.id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    closeDelete();
  }
}
</script>

<template>
  <div class="flex flex-col w-full overflow-hidden bg-white rounded-lg">
    <div class="flex flex-col gap-2.5 p-2 overflow-y-auto max-h-[70vh]">
      <!-- Workspace (public) labels -->
      <h3 class="px-2 text-base font-semibold text-n-slate-12">
        {{ t('LABEL_MGMT.OMNI_POPUP.WORKSPACE_LABELS') }}
      </h3>
      <ul class="flex flex-col gap-0.5 m-0 list-none">
        <li
          v-if="!workspaceLabels.length"
          class="px-2 py-1.5 text-sm text-n-slate-10"
        >
          {{ t('LABEL_MGMT.OMNI_POPUP.NO_LABELS') }}
        </li>
        <li
          v-for="label in workspaceLabels"
          :key="label.id"
          class="flex items-center gap-2 px-2 py-1.5 rounded-md group hover:bg-n-alpha-2"
        >
          <span
            class="flex-shrink-0 rounded-full size-2.5"
            :style="{ backgroundColor: label.color }"
          />
          <div class="flex flex-col min-w-0 flex-1">
            <span class="text-sm truncate text-n-slate-12">{{ label.title }}</span>
            <span
              v-if="label.description"
              class="text-xs truncate text-n-slate-10"
            >
              {{ label.description }}
            </span>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <button
              v-if="hasOpenConversation"
              v-tooltip.top="t('LABEL_MGMT.OMNI_POPUP.ASSIGN_TOOLTIP')"
              class="size-4"
              :class="
                isAssigned(label)
                  ? 'i-lucide-user-check text-n-brand'
                  : 'i-lucide-user-plus text-n-slate-10 hover:text-n-slate-12'
              "
              @click="toggleAssign(label)"
            />
            <button
              v-tooltip.top="t('LABEL_MGMT.OMNI_POPUP.EDIT_TOOLTIP')"
              class="i-lucide-pencil size-3.5 text-n-slate-10 hover:text-n-slate-12"
              @click="emit('edit', label)"
            />
            <button
              v-tooltip.top="t('LABEL_MGMT.OMNI_POPUP.DELETE_TOOLTIP')"
              class="i-lucide-x size-4 text-n-slate-10 hover:text-n-ruby-9"
              @click="openDelete(label)"
            />
          </div>
        </li>
      </ul>

      <!-- Personal (private) labels -->
      <h3 class="px-2 text-base font-semibold text-n-slate-12">
        {{ t('LABEL_MGMT.OMNI_POPUP.PERSONAL_LABELS') }}
      </h3>
      <ul class="flex flex-col gap-0.5 m-0 list-none">
        <li
          v-if="!personalLabels.length"
          class="px-2 py-1.5 text-sm text-n-slate-10"
        >
          {{ t('LABEL_MGMT.OMNI_POPUP.NO_LABELS') }}
        </li>
        <li
          v-for="label in personalLabels"
          :key="label.id"
          class="flex items-center gap-2 px-2 py-1.5 rounded-md group hover:bg-n-alpha-2"
        >
          <span
            class="flex-shrink-0 rounded-full size-2.5"
            :style="{ backgroundColor: label.color }"
          />
          <div class="flex flex-col min-w-0 flex-1">
            <span class="text-sm truncate text-n-slate-12">{{ label.title }}</span>
            <span
              v-if="label.description"
              class="text-xs truncate text-n-slate-10"
            >
              {{ label.description }}
            </span>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <button
              v-if="hasOpenConversation"
              v-tooltip.top="t('LABEL_MGMT.OMNI_POPUP.ASSIGN_TOOLTIP')"
              class="size-4"
              :class="
                isAssigned(label)
                  ? 'i-lucide-user-check text-n-brand'
                  : 'i-lucide-user-plus text-n-slate-10 hover:text-n-slate-12'
              "
              @click="toggleAssign(label)"
            />
            <button
              v-tooltip.top="t('LABEL_MGMT.OMNI_POPUP.EDIT_TOOLTIP')"
              class="i-lucide-pencil size-3.5 text-n-slate-10 hover:text-n-slate-12"
              @click="emit('edit', label)"
            />
            <button
              v-tooltip.top="t('LABEL_MGMT.OMNI_POPUP.DELETE_TOOLTIP')"
              class="i-lucide-x size-4 text-n-slate-10 hover:text-n-ruby-9"
              @click="openDelete(label)"
            />
          </div>
        </li>
      </ul>
    </div>

    <button
      class="px-3 py-2.5 m-1 text-sm font-medium text-left rounded-md text-n-brand hover:bg-n-alpha-2"
      @click="emit('create')"
    >
      {{ t('LABEL_MGMT.OMNI_POPUP.CREATE_NEW_LABEL') }}
    </button>

    <woot-delete-modal
      v-model:show="showDeleteConfirm"
      :on-close="closeDelete"
      :on-confirm="confirmDelete"
      :title="t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </div>
</template>
